import showMessage from "components/showMessage";
import options from "contexts/options.json";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PaginationState, SortingState } from '@tanstack/react-table'
import { CANCEL_STATUS } from "contexts/enviroments";

//--------------------------------------- Newly Added to avoid graphQL.  For auto implement relations ---------------------------------------
import * as bookingRelations from 'app_services/data_sources/mongodb-atlas/Booking/bookings/relationships.json'
import * as dailynoticesRelations from 'app_services/data_sources/mongodb-atlas/Booking/dailynotices/relationships.json'
import * as filesRelations from 'app_services/data_sources/mongodb-atlas/Booking/files/relationships.json'
import * as messagesRelations from 'app_services/data_sources/mongodb-atlas/Booking/messages/relationships.json'
import * as opening_timesRelations from 'app_services/data_sources/mongodb-atlas/Booking/opening_times/relationships.json'
import * as sequenceRelations from 'app_services/data_sources/mongodb-atlas/Booking/sequence/relationships.json'
import * as userRelations from 'app_services/data_sources/mongodb-atlas/Booking/user/relationships.json'
import * as clinicsRelations from 'app_services/data_sources/mongodb-atlas/Booking/clinics/relationships.json'
import * as docksRelations from 'app_services/data_sources/mongodb-atlas/Booking/docks/relationships.json'
import * as patientsRelations from 'app_services/data_sources/mongodb-atlas/Booking/patients/relationships.json'
import * as subscriptionsRelations from 'app_services/data_sources/mongodb-atlas/Booking/subscriptions/relationships.json'
import * as visitsRelations from 'app_services/data_sources/mongodb-atlas/Booking/visits/relationships.json'
import { useFirebaseApp } from "./FirebaseApp";
/**
 * Atlas App Service's relation definition
 */
interface RelationDefinition {
    ref: string;
    source_key: string;
    foreign_key: string;
    is_list: boolean;
}

const relations: { [key:string]: { [key:string]: RelationDefinition  } } = {
    bookings: bookingRelations,
    dailynotices: dailynoticesRelations,
    files: filesRelations,
    messages: messagesRelations,
    opening_times: opening_timesRelations,
    sequence: sequenceRelations,
    user: userRelations,
    clinics: clinicsRelations,
    docks: docksRelations,
    patients: patientsRelations,
    subscriptions: subscriptionsRelations,
    visits: visitsRelations,
}

export const translateEvent = (v: KV): CalendarEvent => {
    const { _id: id, start, end, resourceId, clinicID, modalityID, body1, body2, contrast, checkin, courseName, ivCheckin, clinicName, patientID, patientName, userID, bookingType, bookingMethod, files, comment, walkingStatus, bookingStatus, appointmentMethod, doctor, doctorID, interviewType, dockID, visitID, delay, updated } = v
    return ({
            id, title: options.bookingType[bookingType], start, end, resourceId: v.modalityID, extendedProps: {
            id, clinicID, modalityID, body1, body2, contrast, checkin, courseName, ivCheckin, clinicName, patientID, patientName, userID,
            bookingType, bookingMethod, appointmentMethod, files, comment, walkingStatus, bookingStatus, doctor, doctorID, interviewType, dockID, visitID, delay, updated
        }
    })
}

export const translateEventMonth = (v: KV): CalendarEvent => {
    return ({
        id: v._id.date + v._id.modality, title: v._id.modality, start: v._id.date, end: v._id.date, resourceId: v._id.modality, extendedProps: {
            id: v._id, count: v.count, duration: v.duration
        }
    })
}


export const useFindBookings = (dateRange?: { start?: Date | null, end?: Date | null }, modalities?: KV, noSummarize?: boolean) => {
    const { functions } = useFirebaseApp()
    const startTime = dateRange?.start?.getTime();
    const endTime = dateRange?.end?.getTime();
    const [result, setResult] = useState<{ data?: CalendarEvent[] | null, loading?: boolean, error?: Error | null, refetch?: () => Promise<void> }>({
        data: null,
        loading: false,
        error: null
    })
    const eventsReference = useRef<CalendarEvent[] | null | undefined>([])
    eventsReference.current = result.data

    const fetchData = async () => {
        if (!dateRange || !dateRange.start || !dateRange.end || (modalities?.length === 0)) {
            setResult({
                loading: false,
                data: null,
                error: null,
            });
            return
        }
        setResult({
            loading: true,
            data: null,
            error: null,
        });
        const monthView = noSummarize ? false : (dateRange.end.getTime() - dateRange.start.getTime() > 12096e5)
        try {
            const data = monthView ?
                await functions(["mongo/client", { collection: "bookings", aggregate: [
                    { $match: { start: { $gte: dateRange.start, $lt: dateRange.end }, bookingStatus: { $ne: CANCEL_STATUS }, ...(Array.isArray(modalities) ? { modalityID: { $in: modalities } } : {}) } },
                    {
                        $group: {
                            _id: { date: { $dateTrunc: { date: "$start", unit: "day", timezone: "+09:00" } }, modality: "$modalityID" },
                            duration: { $sum: { $dateDiff: { startDate: "$start", endDate: "$end", unit: "minute", timezone: "+09:00" } } },
                            count: { $sum: 1 }
                        }
                    },
                ]}])
                :
                await functions(["mongo/client", { collection: "bookings", find: { filter: { start: { $gte: dateRange.start, $lt: dateRange.end }, bookingStatus: { $ne: CANCEL_STATUS }, ...(Array.isArray(modalities) ? { modalityID: { $in: modalities } } : {}) }}}])
            setResult({
                data: (data as KV[]).map((v: KV) => monthView ? translateEventMonth(v) : translateEvent(v)),
                loading: false,
                error: null,
                refetch: fetchData
            });
        } catch (err) {
            setResult({
                data: null,
                loading: false,
                error: (err instanceof Error) ? err : null,
            });
        }
        try {
            if (monthView) return; // no watching for month view
            // mongoCollectionWatch = db.collection("bookings").watch({ filter: { $or: [{ operationType: 'delete' }, { 'fullDocument.start': { $gte: dateRange.start, $lt: dateRange.end } }] } });
            // try {
            //     for await (const change of mongoCollectionWatch) {
            //         try {
            //             updateEvent(change)
            //         } catch (err) {
            //             if (err instanceof Error) await showMessage(`予約データ読み込み中にエラーが発生しました。\r\n ${(err as Error).message}`, { error: true })
            //             console.log(`Error updating changed booking data.  ${(err as Error)?.message}`)
            //         }
            //     }
            // } catch (err) {
            //     //error happens on reload.  ignore
            // }
        } catch (err) {
            if (err instanceof Error) await showMessage(`予約データのウオッチ設定中にエラーが発生しました。\r\n ${(err as Error).message}`, { error: true })
        }

    }

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTime, endTime, modalities]) // initially db is null
    return result
}

export const translateVisitEvent = (v: KV): CalendarEvent => {
    return ({
        id: v._id,
        title: options.bookingType[v.bookingType],
        start: v.start,
        end: v.end,
        extendedProps: {
          // _id を二重に持たせるなら id として残す
          id: v._id,
          // 集計で返すキーに合わせる
          patientID: v.patient,
          patientName: v.patientName,
          patientKana: v.patientKana,
          patientGender: v.patientGender,
          patientBirthDate: v.patientBirthDate,
          courseName: v.courseName,
          bookingType: v.bookingType,
          comment: v.comment,
          bookingStatus: v.bookingStatus,
          files: v.files ?? [],
          clinicID: v.clinicID,
          clinicName: v.clinicName,
          ivCheckin: v.ivCheckin,
          doctor: v.doctor,
          doctorID: v.doctorID,
          dockID: v.dockID,
          updated: v.updated
        }
    })
}


export const useFindVisitsNoStream = (dateRange?: { start?: Date | null, end?: Date | null }, modalities?: KV) => {
    const { functions } = useFirebaseApp()
    const startTime = dateRange?.start?.getTime();
    const endTime = dateRange?.end?.getTime();
    const [result, setResult] = useState<{ data?: CalendarEvent[] | null, loading?: boolean, error?: Error | null }>({
        data: null,
        loading: false,
        error: null,
    })
    const eventsReference = useRef<CalendarEvent[] | null | undefined>([])
    eventsReference.current = result.data

    useEffect(() => {
        async function fetchData() {
            if (!dateRange || !dateRange.start || !dateRange.end || (modalities?.length === 0)) {
                setResult({
                    loading: false,
                    data: null,
                    error: null,
                });
                return
            }
            setResult({
                loading: true,
                data: null,
                error: null,
            });
            const monthView = dateRange.end.getTime() - dateRange.start.getTime() > 12096e5
            try {
                const data = monthView ?
                    await functions(["mongo/client", { collection: "visits", aggregate: [
                        { $match: { start: { $gte: dateRange.start, $lt: dateRange.end }, bookingStatus: { $ne: CANCEL_STATUS }, ...(Array.isArray(modalities) ? { modalityID: { $in: modalities } } : {}) } },
                        {
                            $group: {
                                _id: { date: { $dateTrunc: { date: "$start", unit: "day", timezone: "+09:00" } }, modality: "$modalityID" },
                                duration: { $sum: { $dateDiff: { startDate: "$start", endDate: "$end", unit: "minute", timezone: "+09:00" } } },
                                count: { $sum: 1 }
                            }
                        },
                    ]}])
                    :
                    await functions(["mongo/client", { collection: "visits", find: { filter: { start: { $gte: dateRange.start, $lt: dateRange.end }, bookingStatus: { $ne: CANCEL_STATUS }, ...(Array.isArray(modalities) ? { modalityID: { $in: modalities } } : {}) }}}])
                setResult({
                    data: (data as KV[]).map((v: KV) => translateVisitEvent(v)),
                    loading: false,
                    error: null,
                });
            } catch (err) {
                setResult({
                    data: null,
                    loading: false,
                    error: (err instanceof Error) ? err : null,
                });
            }
            try {
                if (monthView) return; // no watching for month view
                // mongoCollectionWatch = db.collection("visits").watch({ filter: { $or: [{ operationType: 'delete' }, { 'fullDocument.start': { $gte: dateRange.start, $lt: dateRange.end } }] } });
                // try {
                //     for await (const change of mongoCollectionWatch) {
                //         try {
                //             updateEvent(change)
                //         } catch (err) {
                //             if (err instanceof Error) await showMessage(`予約データ読み込み中にエラーが発生しました。\r\n ${(err as Error).message}`, { error: true })
                //             console.log(`Error updating changed visit data.  ${(err as Error)?.message}`)
                //         }
                //     }
                // } catch (err) {
                //     //error happens on reload.  ignore
                // }
            } catch (err) {
                if (err instanceof Error) await showMessage(`予約データのウオッチ設定中にエラーが発生しました。\r\n ${(err as Error).message}`, { error: true })
            }

        }
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTime, endTime, modalities]) // initially db is null
    return result
}

export const useFindVisits = (dateRange?: { start?: Date | null, end?: Date | null }, modalities?: KV) => {
    const { functions } = useFirebaseApp()
    const startTime = dateRange?.start?.getTime();
    const endTime = dateRange?.end?.getTime();
    const eventsReference = useRef<CalendarEvent[] | null | undefined>([])

    // Put currentModalities null to cancel modalities setting and select all modalities.  If undefined, modalities setting is used.
    const fetchData = async (currentDateRange?: { start?: Date | null, end?: Date | null }, currentModalities?: KV) => {
        const actualDateRange = currentDateRange || dateRange
        const actualModalities = currentModalities === undefined ? modalities : currentModalities
        if (!actualDateRange || !actualDateRange.start || !actualDateRange.end || (actualModalities?.length === 0)) {
            setResult(current => ({ ...current,
                loading: false,
                data: null,
                error: null,
            }));
            return
        }
        setResult(current => ({ ...current,
            loading: true,
            data: null,
            error: null,
        }));
        const monthView = actualDateRange.end.getTime() - actualDateRange.start.getTime() > 12096e5
        try {
            const data = monthView ?
                await functions(["mongo/client", { collection: "visits", aggregate: [
                    { $match: { start: { $gte: actualDateRange.start, $lt: actualDateRange.end }, bookingStatus: { $ne: CANCEL_STATUS }, ...(Array.isArray(actualModalities) ? { modalityID: { $in: actualModalities } } : {}) } },
                    {
                        $group: {
                            _id: { date: { $dateTrunc: { date: "$start", unit: "day", timezone: "+09:00" } }, modality: "$modalityID" },
                            duration: { $sum: { $dateDiff: { startDate: "$start", endDate: "$end", unit: "minute", timezone: "+09:00" } } },
                            count: { $sum: 1 }
                        }
                    },
                ]}])
                :
                await functions(["mongo/client", { collection: "visits", aggregate: [
                  /* 1) 条件で絞り込み */
                  {
                    $match: {
                      start: { $gte: actualDateRange.start, $lt: actualDateRange.end },
                      bookingStatus: { $ne: CANCEL_STATUS },
                      ...(Array.isArray(actualModalities)
                        ? { modalityID: { $in: actualModalities } }
                        : {})
                    }
                    },
                  /* 2) 先頭ゼロを除去して文字列に → "10883" 形式 */
                  {
                    $addFields: {
                      patientIdForJoin: { $toString: { $toLong: "$patient" } }  // ← "0000014706" → "14706"
                    }
                    },
                  /* 3) patients と結合（patientId で突合）*/
                  {
                    $lookup: {
                      from: "patients",
                      localField: "patientIdForJoin",
                      foreignField: "patientId",
                      as: "patientInfo"
                    }
                  },
                  {
                    $unwind: {
                      path: "$patientInfo",
                      preserveNullAndEmptyArrays: true
                    }
                    },
                  /* 4) 必要な患者情報を上書き・追加 */
                  {
                    $addFields: {
                      patientName: "$patientInfo.name",
                      patientKana: "$patientInfo.nameKana",
                      patientGender: "$patientInfo.gender",
                      patientBirthDate: "$patientInfo.birthDate"
                    }
                    },
                  /* 5) 出力したい列だけ残す（patientIdForJoin など余分な列は除外） */
                  {
                      $project: {
                        /* visits 側の主な列 */
                        _id:           1,
                        bookingIDs:    1,
                        courseName:    1,
                        bookingType:   1,
                        start:         1,
                        end:           1,
                        comment:       1,
                        patientID: "$patient",
                        patient:       1,
                        dockID: 1,
                        clinicID: "$clinicID",
                        clinicName: "$clinicName",
                        ivCheckin: "$ivCheckin",
                        doctor: "$doctor",
                        doctorID: "$doctorID",
                        files:         1,
                        updated: 1,
                        bookingStatus: "$bookingStatus",
                        /* patients から補完した列 */
                        patientName:        1,
                        patientKana:        1,
                        patientGender:      1,
                        patientBirthDate: 1,
                        patientIdForJoin: 1
                    }
                  }
                ]}]);

            setResult(current => ({ ...current,
                data: (data as KV[]).map((v) => translateVisitEvent(v)),
                loading: false,
                error: null,
            }));
        } catch (err) {
            setResult(current => ({ ...current,
                data: null,
                loading: false,
                error: (err instanceof Error) ? err : null,
            }));
        }
    }

    const [result, setResult] = useState<{ data?: CalendarEvent[] | null, loading?: boolean, error?: Error | null, refetch: (dateRange?: { start?: Date | null, end?: Date | null }, modalities?: KV) => Promise<void> }>({
        data: null,
        loading: false,
        error: null,
        refetch: fetchData
    })
    eventsReference.current = result.data


    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startTime, endTime, modalities]) // initially db is null
    return result
}

//--------------------------------------- Newly Added to avoid graphQL ---------------------------------------
// const client = new MongoClient(process.env.REACT_APP_MONGO_URL || '');


const parseRelationDefinition = async (relationDefinition: RelationDefinition, pipeline: KV[] = []):Promise<any[]> => {
    if (
      relationDefinition.ref &&
      relationDefinition.source_key &&
      relationDefinition.foreign_key
    ) {
      const refParts = relationDefinition.ref.split("/");
      if (refParts.length !== 5 || refParts[0] !== "#" || refParts[1] !== "relationship" || refParts[2] !== "mongodb-atlas") {
        throw new Error("Invalid ref format");
      }
      const col_name = refParts[4];
  
      const lookupStage: any = {
        $lookup: {
            from: col_name,
            localField: relationDefinition.source_key,
            foreignField: relationDefinition.foreign_key,
            as: relationDefinition.source_key,
        },
      };
      pipeline.push(lookupStage);
      if (!relationDefinition.is_list) {
        pipeline.push({ $unwind: {
            path: `$${relationDefinition.source_key}`,
            preserveNullAndEmptyArrays: true
        }});
      }
    }
    return pipeline;
}

/**
 * Alternative for Apollo Client's query in MongoDB
 * @returns 
 */
export const useMongoQueryDirect = () => {
    const { functions } = useFirebaseApp()
    const fetch = async <T extends KV | KV[] = KV> (collection:string, query:KV, isMany:boolean = false, options:KV = {}):Promise<{ data?:T, error?:Error }> => {
        try {
            // await client.connect();
            // const db = client.db('Booking');
            if (options.skip) {
                return { data:undefined, error: undefined }
            }
            const relation = Object.keys(relations[collection]).length ? relations[collection] : null;
            if (relation) {
                const pipeline:KV[] = [{ $match: query }];
                Object.keys(relation).forEach(key => parseRelationDefinition(relation[key], pipeline))
                pipeline.push({ $limit: 1 });
                const results = await functions(["mongo/client", { collection: collection, aggregate: pipeline }]);
                return isMany ? { data: results as T, error: undefined } : { data: (results as KV[])[0] as T, error: undefined }
            } else {
                const result = isMany ? (await functions(["mongo/client", { collection: collection, find: query, options: options }])) : (await functions(["mongo/client", { collection: collection, findOne: query, options: options }]));
                return { data:(result as T), error: undefined };
            }
        } catch (err) {
            if (err instanceof Error) return { data: undefined, error: err};
        }
        return { data: undefined, error: undefined };
    }
    return fetch;
}

/**
 * Alternative for useQuery of Apollo Client in MongoDB
 * @param collection Collection name to query
 * @param query Same as query in find function
 * @param options Same as options in find function
 * @param onCompleted Function executed after getting result
 * @returns 
 */
export const useMongoQuery = (collection:string, query:KV, options:KV = {}, onCompleted?: (result:KV) => void) => {
    const { functions } = useFirebaseApp()
    const [memoizedQuery, memorizedOption] = useMemo(() => [query, options], [JSON.stringify(query), JSON.stringify(options)]);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<KV|undefined>();
    const [error, setError] = useState<Error>();
    const refetch = async () => {
        setLoading(true);
        setData(undefined)
        setError(undefined);
        try {
            // await client.connect();
            // const db = client.db('Booking');
            if (options.skip) {
                setLoading(false)
                return
            }
            const relation = Object.keys(relations[collection]).length ? relations[collection] : null;
            if (relation) {
                const pipeline:KV[] = [{ $match: query }];
                Object.keys(relation).forEach(key => parseRelationDefinition(relation[key], pipeline))
                pipeline.push({ $limit: 1 });
                const results = await functions(["mongo/client", { collection: collection, aggregate: pipeline }]);
                setData((results as KV[])[0])
                onCompleted && onCompleted((results as KV[])[0])
            } else {
                const result = await functions(["mongo/client", { collection: collection, findOne: { filter: query, options: options }}]);
                setData(result as KV);
                onCompleted && onCompleted(result as KV)
            }
        } catch (err) {
            if (err instanceof Error) setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collection, memoizedQuery, memorizedOption]);
    
    return { loading, data, error, refetch };
}

/**
 * Alternative for useQuery(Multiple results) of Apollo Client in MongoDB
 * @param collection Collection name to query
 * @param query Same as query in find function
 * @param options Same as options in find function
 * @param onCompleted Function executed after getting result
 * @returns 
 */
export const useMongoQueries = (
  collection: string,
  queryOrPipeline: KV, // query か pipeline
  options: KV = {},
  onCompleted?: (results: KV[]) => void
) => {
    const { functions } = useFirebaseApp()
    const [memoizedInput, memoizedOption] = useMemo(
        () => [queryOrPipeline, options],
        [JSON.stringify(queryOrPipeline), JSON.stringify(options)]
    );

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<KV[]|undefined>();
    const [error, setError] = useState<Error>();

    const refetch = async () => {
        setLoading(true);
        setData(undefined)
        setError(undefined);
        try {
            // await client.connect();
            // const db = client.db('Booking');
            if (options.skip) {
                setLoading(false)
                return
            }

            const isPipeline = Array.isArray(queryOrPipeline);

            if (isPipeline) {
                const results = await functions(["mongo/client", { collection: collection, aggregate: queryOrPipeline }]);
                setData(results as KV[]);
                onCompleted && onCompleted(results as KV[]);
            } else {
                const relation = Object.keys(relations[collection] ?? {}).length ? relations[collection] : null;
                if (relation) {
                    const pipeline: KV[] = [{ $match: queryOrPipeline }];
                    Object.keys(relation).forEach((key) =>
                        parseRelationDefinition(relation[key], pipeline)
                    );
                    const results = await functions(["mongo/client", { collection: collection, aggregate: pipeline }]);
                    setData(results as KV[]);
                    onCompleted && onCompleted(results as KV[]);
                } else {
                    const results = await functions(["mongo/client", { collection: collection, find: { filter: queryOrPipeline, options: options }}]);
                    setData(results as KV[]);
                    onCompleted && onCompleted(results as KV[]);
                }
            }
        } catch (err) {
            if (err instanceof Error) setError(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collection, memoizedInput, memoizedOption]);
    
    return { loading, data, error, refetch };
}

/**
 * Alternative for useLazyQuery of Apollo Client in MongoDB
 * @param collection Collection name to query
 * @param options Same as options in find function
 * @param onCompleted Function executed after getting result
 * @returns 
 */
export const useMongoLazyQuery = (collection:string, options:KV = {}, onCompleted?: (result:KV) => void):[(query:KV) => Promise<void>, { called: boolean, loading:boolean, data?:KV, error?:Error}] => {
    const { functions } = useFirebaseApp()
    const [called, setCalled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<KV|undefined>();
    const [error, setError] = useState<Error>();
    const refetch = async (query:KV) => {
        setCalled(true);
        setLoading(true);
        setData(undefined);
        setError(undefined);
        try {
            // await client.connect();
            // const db = client.db('Booking');
            if (options.skip) {
                setLoading(false)
                return
            }
            const relation = Object.keys(relations[collection]).length ? relations[collection] : null;
            if (relation) {
                const pipeline: KV[] = [{ $match: query }];
                Object.keys(relation).forEach(key => parseRelationDefinition(relation[key], pipeline))
                pipeline.push({ $limit: 1 });
                const results = await functions(["mongo/client", { collection: collection, aggregate: pipeline }]);
                setData((results as KV[])[0])
                onCompleted && onCompleted((results as KV[])[0])
            } else {
                const result = await functions(["mongo/client", { collection: collection, findOne: { filter: query, options: options }}]);
                setData(result as KV);
                onCompleted && onCompleted(result as KV)
            }
        } catch (err) {
            if (err instanceof Error) setError(err);
        } finally {
            setLoading(false);
        }
    };

    return [refetch, { called, loading, data, error }];
}

/**
 * Alternative for useLazyQuery(Multiple results) of Apollo Client in MongoDB
 * @param collection Collection name to query
 * @param options Same as options in find function
 * @param onCompleted Function executed after getting result
 * @returns 
 */
export const useMongoLazyQueries = (collection:string, options:KV = {}, onCompleted?: (results:KV[]) => void):[(query:KV) => Promise<void>, { called: boolean, loading:boolean, data?:KV, error?:Error}] => {
    const { functions } = useFirebaseApp()
    const [called, setCalled] = useState(false);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<KV[]|undefined>();
    const [error, setError] = useState<Error>();
    const refetch = async (query:KV) => {
        setCalled(true);
        setLoading(true);
        setData(undefined)
        setError(undefined);
        try {
            // await client.connect();
            // const db = client.db('Booking');
            if (options.skip) {
                setLoading(false)
                return
            }
            const relation = Object.keys(relations[collection]).length ? relations[collection] : null;
            if (relation) {
                const pipeline:KV[] = [{ $match: query }];
                Object.keys(relation).forEach(key => parseRelationDefinition(relation[key], pipeline))
                const results = await functions(["mongo/client", { collection: collection, aggregate: pipeline }]);
                setData(results as KV[]);
                onCompleted && onCompleted(results as KV[]);
            } else {
                // const cursor = db.collection(collection).find(query, options);
                // const results = await cursor.toArray();
                const results = await functions(["mongo/client", { collection: collection, find: { filter: query, options: options }}]);
                setData(results as KV[]);
                onCompleted && onCompleted(results as KV[]);
            }
        } catch (err) {
            if (err instanceof Error) setError(err);
        } finally {
            setLoading(false);
        }
    };

    return [refetch, { called, loading, data, error }];
}

/**
 * Alternative for useMutation(update) of Apollo Client in MongoDB
 * @param collection Collection name to mutate
 * @param isMany Set true if updating multiple documents
 * @param options Same as options in update function
 * @param onCompleted Function executed after update
 * @returns In data, returns document in single update.
 */
export const useMongoUpdate = (collection:string, isMany: boolean = false, options:KV = {}, onCompleted?: () => void) => {
    const { functions } = useFirebaseApp()
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<KV>();
    const [error, setError] = useState<Error>();

    const mutate = async (updateQuery:KV, updateData:KV) => {
        setLoading(true);
        setData(undefined);
        setError(undefined);
        let result:KV|undefined;
        try {
            // await client.connect();
            // const db = client.db('Booking');
            if (isMany) await functions(["mongo/client", { collection: collection, updateMany: { filter: updateQuery, update: { $set: updateData }, options: options }}]);
            else {
                result = await functions(["mongo/client", { collection: collection, findOneAndUpdate: { filter: updateQuery, update: { $set: updateData }, options: options }}]) as KV;
                // result = await db.collection(collection).findOneAndUpdate(updateQuery, updateData, options);
                setData(result);
            }
            onCompleted && onCompleted()
        } catch (err) {
            if (err instanceof Error) setError(err);
        } finally {
          setLoading(false);
        }
        return { data: result }
    };
      return [mutate, { loading, data, error }] as [(updateQuery:KV, updateData:KV) => Promise<{ data?: KV }>, { loading: boolean; data?: KV; error?: Error; }];

}

/**
 * Alternative for useMutation(add) of Apollo Client in MongoDB
 * @param collection Collection name to mutate
 * @param onCompleted Function executed after add
 * @returns _id of document is returned as id when mutate is executed.
 */
export const useMongoAdd = (collection:string, onCompleted?: () => void) => {
    const { functions } = useFirebaseApp()
    const [loading, setLoading] = useState(false);
    const [id, setId] = useState<{ $oid: string } | string>();
    const [error, setError] = useState<Error>();

    const mutate = async (insertData:KV) => {
        setLoading(true);
        setId(undefined);
        setError(undefined);
        try {
            if (collection === 'bookings') {
                const nowPlus2Days = new Date();
                nowPlus2Days.setDate(nowPlus2Days.getDate() + 2);
                // dueやreportDueDateがDate型でない場合は、Date型に変換, 2日後に設定
                const isValidDate = (d: any): d is Date =>
                    d instanceof Date && !isNaN(d.getTime());

                // due チェック
                if (!isValidDate(insertData.due)) {
                    insertData.due = nowPlus2Days;
                }

                // report チェック
                if (!insertData.report || typeof insertData.report !== "object") {
                    insertData.report = {};
                }

                if (!isValidDate(insertData.report.dueDate)) {
                    insertData.report.dueDate = nowPlus2Days;
                }

            }
            const result = await functions(["mongo/client", { collection: collection, insertOne: { doc: insertData } }]) as KV;
            setId(result.insertedId);
            onCompleted && onCompleted()
            return { addedBooking: result.insertedId }
        } catch (err) {
            if (err instanceof Error) setError(err);
        } finally {
          setLoading(false);
        }
      };
    
      return [mutate, { loading, id, error }] as [(insertData:KV) => Promise<void>, { loading: boolean; id?: string | { $oid: string }; error?: Error; }];

}

/**
 * Alternative for useMutation(add multiple) of Apollo Client in MongoDB
 * @param collection Collection name to mutate
 * @param onCompleted Function executed after add
 * @returns _id of document is returned as id when mutate is executed.
 */
export const useMongoAdds = (collection:string, onCompleted?: () => void) => {
    const { functions } = useFirebaseApp()
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<KV>();
    const [error, setError] = useState<Error>();

    const mutate = async (insertData:KV[]) => {
        setLoading(true);
        setResult(undefined);
        setError(undefined);
        try {
            const insertResult = await functions(["mongo/client", { collection: collection, insertMany: { docs: insertData } }]) as KV;
            setResult(insertResult);
            onCompleted && onCompleted()
        } catch (err) {
            if (err instanceof Error) setError(err);
        } finally {
          setLoading(false);
        }
      };
    
      return [mutate, { loading, result, error }] as [(insertData:KV) => Promise<void>, { loading: boolean; result?:KV; error?: Error; }];

}

/**
 * Alternative for useMutation(delete) of Apollo Client in MongoDB
 * @param collection Collection name to mutate
 * @param isMany Set true if deleting multiple documents
 * @param options Same as options in delete function
 * @param onCompleted Function executed after delete
 * @returns In data, returns deleted document in single delete.
 */
export const useMongoDelete = (collection:string, isMany: boolean = false, options:KV = {}, onCompleted?: () => void) => {
    const { functions } = useFirebaseApp()
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<KV>();
    const [error, setError] = useState<Error>();

    const mutate = async (deleteQuery:KV) => {
        setLoading(true);
        setData(undefined)
        setError(undefined);
        try {
            // await client.connect();
            // const db = client.db('Booking');
            if (isMany) await functions(["mongo/client", { collection: collection, deleteMany: { filter: deleteQuery, options: options }}]);
            else {
                const result = await functions(["mongo/client", { collection: collection, findOneAndDelete: { filter: deleteQuery, options: options }}]) as KV;
                setData(result);
            }
            onCompleted && onCompleted();
        } catch (err) {
            if (err instanceof Error) setError(err);
        } finally {
          setLoading(false);
        }
      };
    
      return [mutate, { loading, data, error }] as [(deleteQuery:KV) => Promise<void>, { loading: boolean; data?: KV; error?: Error; }];

}

// ---------------------------------------------------------------------------
// Shared paginated data hook (moved from hooks/useData.ts)
// ---------------------------------------------------------------------------

export type UsePaginatedDataConfig<TData = KV> = {
    /** MongoDB collection name */
    collection: string
    /**
     * Function that builds the base aggregation pipeline stages.
     * This should include all $match, $lookup, and other stages except $sort, $skip, and $limit.
     *
     * @param filterValues - Current filter values that might affect the query
     * @returns Array of MongoDB aggregation pipeline stages
     */
    buildBaseStages: (filterValues: KV) => KV[]
    /**
     * Optional mapping from column IDs (used in sorting) to MongoDB field names.
     * If not provided, column IDs are used directly as MongoDB field paths.
     * Use this only when column IDs differ from MongoDB field names.
     */
    columnIdToFieldMap?: Record<string, string>
    /** Current filter values object */
    filterValues: KV
    /** Initial page size (default: 100) */
    initialPageSize?: number
}

export type UsePaginatedDataReturn<TData = KV> = {
    /** Current page data */
    data: TData[]
    /** Loading state */
    loading: boolean
    /** Total count of all matching records */
    totalCount: number
    /** Current pagination state */
    pagination: PaginationState
    /** Current sorting state */
    sorting: SortingState
    /** Handler to update pagination state */
    handlePaginationChange: (pagination: PaginationState) => void
    /** Handler to update sorting state */
    handleSortingChange: (sorting: SortingState) => void
    /** Manual refetch function */
    refetch: () => void
}

/**
 * Custom hook for fetching paginated and sortable data from MongoDB.
 *
 * This is the renamed and relocated version of the previous `useData` hook.
 */
export function usePaginatedData<TData = KV>(
    config: UsePaginatedDataConfig<TData>
): UsePaginatedDataReturn<TData> {
    const { functions } = useFirebaseApp()
    const {
        collection,
        buildBaseStages,
        columnIdToFieldMap,
        filterValues,
        initialPageSize = 100,
    } = config

    const [data, setData] = useState<TData[]>([])
    const [loading, setLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: initialPageSize,
    })
    const [sorting, setSorting] = useState<SortingState>([])

    // Store config values in refs to avoid dependency issues
    const buildBaseStagesRef = useRef(buildBaseStages)
    const columnIdToFieldMapRef = useRef(columnIdToFieldMap)
    buildBaseStagesRef.current = buildBaseStages
    columnIdToFieldMapRef.current = columnIdToFieldMap

    const fetchData = useCallback(
        async (
            pageIndex: number,
            pageSize: number,
            sortState: SortingState,
            currentFilterValues: KV
        ) => {
            setLoading(true)
            try {
                // Debug: track fetch parameters for paginated data
                // eslint-disable-next-line no-console
                console.log('[usePaginatedData] fetchData', {
                    collection,
                    pageIndex,
                    pageSize,
                    sortState,
                    currentFilterValues,
                })
                // Build base aggregation pipeline stages
                const baseStages = buildBaseStagesRef.current(currentFilterValues)

                // Get total count
                const countStages = [...baseStages, { $count: 'total' }]
                const countResult = (await functions([
                    'mongo/client',
                    { collection, aggregate: { pipeline: countStages } },
                ])) as KV[]
                const total = countResult[0]?.total || 0
                setTotalCount(total)

                // Build sort stage if sorting is specified
                const sortStages: KV[] = []
                if (sortState.length > 0) {
                    const sortObject: KV = {}
                    const mapping = columnIdToFieldMapRef.current
                    sortState.forEach(sort => {
                        const fieldName = mapping ? (mapping[sort.id] || sort.id) : sort.id
                        sortObject[fieldName] = sort.desc ? -1 : 1
                    })
                    sortStages.push({ $sort: sortObject })
                }

                // Get paginated data
                const dataStages = [
                    ...baseStages,
                    ...sortStages,
                    { $skip: pageIndex * pageSize },
                    { $limit: pageSize },
                ]
                const result = (await functions([
                    'mongo/client',
                    { collection, aggregate: { pipeline: dataStages } },
                ])) as TData[]
                setData(result)
            } catch (error) {
                console.error('Error fetching data:', error)
                setData([])
                setTotalCount(0)
            } finally {
                setLoading(false)
            }
        },
        [collection, functions]
    )

    // Normalize filter values for dependency comparison (handle Date objects)
    const filterValuesKey = useMemo(() => {
        const normalized: KV = {}
        for (const [key, value] of Object.entries(filterValues)) {
            if (value instanceof Date) {
                normalized[key] = value.getTime()
            } else {
                normalized[key] = value
            }
        }
        return JSON.stringify(normalized)
    }, [filterValues])

    // Reset to first page when filters change
    useEffect(() => {
        setPagination(prev => ({ ...prev, pageIndex: 0 }))
    }, [filterValuesKey])

    // Fetch data when pagination, sorting, or filters change
    useEffect(() => {
        fetchData(pagination.pageIndex, pagination.pageSize, sorting, filterValues)
    }, [pagination.pageIndex, pagination.pageSize, sorting, filterValuesKey, fetchData])

    const handlePaginationChange = useCallback((newPagination: PaginationState) => {
        setPagination(newPagination)
    }, [])

    const handleSortingChange = useCallback((newSorting: SortingState) => {
        setSorting(newSorting)
    }, [])

    // Store current filter values in ref for refetch
    const filterValuesRef = useRef(filterValues)
    filterValuesRef.current = filterValues

    const refetch = useCallback(() => {
        fetchData(pagination.pageIndex, pagination.pageSize, sorting, filterValuesRef.current)
    }, [pagination.pageIndex, pagination.pageSize, sorting, fetchData])

    return {
        data,
        loading,
        totalCount,
        pagination,
        sorting,
        handlePaginationChange,
        handleSortingChange,
        refetch,
    }
}
